import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/**
 * Org (CDN) — `GET /api/v3/orgs`. Carries account-level entitlement flags; the
 * console uses `waf_enabled` to show/hide the WAF surfaces (WAF + WAF Analytics).
 * Ported from the legacy partner-console `External.SwiftFederation/connectors/orgs`.
 */
export interface Org {
  id: number;
  name: string;
  slug: string;
  description: string;
  is_system: boolean;
  max_domains: number;
  enabled: boolean;
  user_count: number;
  domain_count: number;
  waf_enabled: boolean;
  parent_id: number;
}

interface Envelope<T> {
  data: T;
  message?: string;
}

export const orgKeys = {
  all: ['v3', 'orgs'] as const,
};

export function useOrgs() {
  return useQuery({
    queryKey: orgKeys.all,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Org[]> => {
      const res = await serviceApiV3.get<Envelope<Org[]>>('/orgs');
      return res.data?.data ?? [];
    },
  });
}

/** True when any org the user belongs to has WAF entitlement. */
export function useWafEnabled(): boolean {
  const { data } = useOrgs();
  return data?.some((o) => o.waf_enabled) ?? false;
}
